import { z } from "zod";
import { zfd } from "zod-form-data";

export const WaitListFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

export type IWaitListFormInput = z.infer<typeof WaitListFormSchema>;

export const SubscriptionFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

export type ISubscriptionFormInput = z.infer<typeof WaitListFormSchema>;

export const OAuthFormSchema = z.object({
  provider: z.string().min(1, { message: "Provider name is required" }),
  callbackUrl: z.string().optional(),
});

export type IOAuthFormInput = z.infer<typeof OAuthFormSchema>;

// Define the validation schema using Zod
export const ToolSubmitFormClientSchema = z
  .object({
    title: z.string().min(1, "Title is required"),
    tagline: z.string().min(1, "Tagline is required"),
    contentMarkdown: z.string().min(1, "Content in markdown format is required"),
    url: z.string().url("Must be a valid URL"),
    priceValue: z.preprocess((val) => parseFloat(val as string), z.number({ message: "Price value must be a number" })),
    priceDuration: z.string().min(1, "Price Duration is required"),
    socialLinks: z.array(
      z.object({
        link: z.string().url("Must be a valid URL"),
      })
    ),
    youtubeVideoUrls: z.array(
      z.object({
        link: z.string().url("Must be a valid URL"),
      })
    ),
    logo: z.any().refine((file) => file?.length > 0, "Logo is required"),
    screenshots: z.array(z.any()).refine((value) => value.length > 0 && value[0].file?.length > 0, "At least one screenshot is required"),
    priceType: z.string().min(1, "Price type is required"),
    topics: z
      .array(
        z.object({
          title: z.string(),
          slug: z.string(),
          name: z.string(),
          tag_type: z.string(),
          emoji: z.string(),
          value: z.string(),
          label: z.string(),
        })
      )
      .min(1, "At least one topic is required"),
  })
  .partial()
  .refine(
    (data) => {
      if (["free", "open_source", "contact_for_pricing"].includes(data?.priceType!)) {
        return Number(data?.priceValue) === 0;
      } else {
        return Number(data?.priceValue) > 0;
      }
    },
    {
      message: "Invalid price value for the selected price type",
      path: ["priceValue"],
    }
  );

export type IToolSubmitFormInput = z.infer<typeof ToolSubmitFormClientSchema>;

export const ToolSubmitFormServerSchema = zfd.formData({
  title: zfd.text(),
  tagline: zfd.text(),
  contentMarkdown: zfd.text(),
  url: zfd.text(),
  priceType: zfd.text(),
  priceValue: zfd.text(), // Assuming priceValue can be optional
  priceDuration: zfd.text(),
  topics: zfd.repeatableOfType(z.string()),
  logo: zfd.file(), // Assuming logo is a File object
  screenshots: zfd.repeatableOfType(zfd.file()), // Assuming screenshots is an array of File objects and can be optional
  youtubeVideoUrls: zfd.repeatableOfType(z.string()).optional(), // Assuming youtubeVideoUrls is an array of strings and can be optional
  socialLinks: zfd.repeatableOfType(z.string()).optional(), // Assuming socialLinks is an array of strings and can be optional
});

export type IToolSubmitFormServerInput = z.infer<typeof ToolSubmitFormServerSchema>;

export const GPTStoreSubmitFormSchema = z.object({
  website: z.string().url("Must be a valid URL"),
});

export type IGPTStoreSubmitFormInput = z.infer<typeof GPTStoreSubmitFormSchema>;

export const JobFormClientSchema = z
  .object({
    jobId: z.string().optional(),
    jobTitle: z.string().min(1, "Job title is required"),
    category: z.string().min(1, "Category is required"),
    timezones: z.array(z.string()).optional(),
    skills: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        })
      )
      .min(1, "At least one skill is required"),
    regions: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        })
      )
      .min(1, "At least one region is required"),
    benefits: z.array(z.string()).min(1, "At least one benefit is required"),
    jobType: z.string().min(1, "Job type is required"),
    description: z.string().min(1, "Description is required"),
    salaryRange: z.string().min(1, "Salary range is required"),
    applyUrl: z.string().url().min(1, "Apply URL is required"),
    jobReachEmail: z.string(),
    // Optional company fields
    companyId: z.string().optional(),
    companyName: z.string().min(1, "Company name is required"),
    companyLocation: z.string().min(1, "Company location is required"),
    companyWebsite: z.string().url().min(1, "Company website is required"),
    companyLogoUrl: z.string().optional(),
    companyLogo: z
      .any()
      .optional()
      .refine((file) => !file || file?.length > 0, {
        message: "Company logo is required",
      }),
    companyDescription: z.string().optional(),
  })
  .partial()
  .refine(
    (data) => {
      return data.companyLogo || data.companyLogoUrl;
    },
    {
      path: ["companyLogo"],
      message: "Company logo is required",
    }
  )
  .superRefine((args, ctx) => {
    if (args.jobReachEmail || args.applyUrl) {
      return true;
    }
    return ctx.addIssue({
      fatal: true,
      path: ["applyUrl"],
      message: "Job Apply URL or Job Reach Email is required",
      code: z.ZodIssueCode.custom,
    });
  });

export type IJobFormClientInput = z.infer<typeof JobFormClientSchema>;

export const JobFormServerSchema = zfd
  .formData({
    jobId: z.string().optional(),
    jobTitle: zfd.text(),
    category: zfd.text(),
    skills: zfd.repeatableOfType(z.string()),
    regions: zfd.repeatableOfType(z.string()),
    benefits: zfd.repeatableOfType(z.string()),
    jobType: zfd.text(),
    description: zfd.text(),
    salaryRange: zfd.text(),
    applyUrl: z.string().optional(),
    jobReachEmail: z.string().optional(),
    companyId: z.string().optional(),
    companyName: zfd.text(),
    companyLocation: zfd.text(),
    companyWebsite: zfd.text(),
    companyLogo: z.any().optional(),
    companyLogoUrl: z.string().optional(),
    companyDescription: z.string().optional(),
  })
  .refine((data: any) => data.companyLogo || data.companyLogoUrl, {
    path: ["companyLogo"],
    message: "Company logo is required",
  })
  .refine((data: any) => data.jobReachEmail || data.applyUrl, {
    path: ["jobReachEmail", "applyUrl"],
    message: "Job Apply URL or Job Reach Email is required",
  });

export type IJobFormServerInput = z.infer<typeof JobFormServerSchema>;
